import { API_BASE_URL, fetchApi } from './api';

const BASE = 'supply-agent';

export interface SupplyAgentOrderSnapshot {
  total_items: number;
  total_lines: number;
  total_amount_euros: number;
  lines_detail?: Record<string, unknown>[];
}

export interface SupplyAgentChangeReason {
  label: string;
  step?: string;
  rule_type?: string;
  rule_id?: number;
}

export interface SupplyAgentLineChange {
  product_id: number;
  sku: string;
  product_name?: string;
  supplier_id?: number;
  supplier_name?: string;
  family_name?: string;
  old_quantity: number;
  new_quantity: number;
  old_value_euros: number;
  new_value_euros: number;
  reasons?: SupplyAgentChangeReason[];
}

export interface SupplyAgentSimulationSummary {
  before_value_euros: number;
  after_value_euros: number;
  delta_value_euros: number;
  before_units: number;
  after_units: number;
  delta_units: number;
  lines_changed: number;
  applied_rules_count: number;
  applied_rule_ids?: number[];
  applied_rule_labels?: string[];
  top_reasons?: Array<{
    label: string;
    rule_type?: string;
    rule_id?: number;
    lines_affected: number;
  }>;
  strategy_explanations?: string[];
  completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
  rule_contributions?: Array<{
    label: string;
    rule_type?: string;
    rule_id?: number;
    lines_affected: number;
    contribution: 'lines' | 'order_satisfied' | 'order_unmet' | 'active';
  }>;
}

export interface SupplyAgentFamilySupplierSlice {
  supplier_id: number;
  supplier_name: string;
  units: number;
}

export interface SupplyAgentFamilyGlobalSummary {
  family_names?: string[];
  global_units?: number;
  global_units_outside?: number;
  local_units_after?: number;
  cap?: number | null;
  cut_here?: number;
  remaining_elsewhere?: number;
  family_maximum_quantity?: number | null;
  family_minimum_quantity?: number | null;
  family_minimum_order_value?: number | null;
  global_value?: number;
  scope?: 'warehouse' | 'supplier' | string;
  by_supplier?: SupplyAgentFamilySupplierSlice[];
}

export interface SupplyAgentFamilyOverviewResponse {
  organization_id: number;
  warehouse_id: number;
  families: Array<{
    rule_id: number;
    rule_type: string;
    name: string;
    family_names: string[];
    /** Umbral (mín o máx). Alias histórico de threshold. */
    cap: number | null;
    threshold?: number | null;
    global_units: number;
    global_value?: number;
    gap_to_min?: number;
    excess_over_max?: number;
    status?: 'below_min' | 'over_max' | 'ok' | string;
    by_supplier: SupplyAgentFamilySupplierSlice[];
  }>;
  count: number;
}

export interface SupplyAgentWarehouseSupplierImpact {
  supplier_id: number;
  supplier_name: string;
  before_value_euros: number;
  residual_cap_euros: number | null;
  share_minimum_euros?: number | null;
  after_value_euros: number;
  delta_value_euros: number;
  before_units?: number;
  after_units?: number;
  delta_units?: number;
}

export interface SupplyAgentWarehouseSimulateResponse {
  status: string;
  organization_id?: number;
  warehouse_id: number;
  budget_limit_euros: number | null;
  minimum_order_euros?: number | null;
  units_cap?: number | null;
  before_value_euros: number;
  after_value_euros: number;
  delta_value_euros: number;
  excess_before_euros: number;
  gap_to_minimum_euros?: number;
  before_units?: number;
  after_units?: number;
  delta_units?: number;
  excess_before_units?: number;
  by_supplier: SupplyAgentWarehouseSupplierImpact[];
  applied_rules: Array<{
    id?: number;
    rule_type: string;
    name: string;
    amount?: number;
    units?: number;
  }>;
}

export interface SupplyAgentCommitmentSummary {
  unit: 'units' | 'EUR' | string;
  period_target: number;
  ytd: number;
  remaining: number;
  months_left: number;
  order_target: number;
  year: number;
  distribution?: string;
  current_order?: number;
  gap_to_order_target?: number;
  status?: 'fulfilled' | 'on_track' | 'needs_top_up' | string;
}

export interface SupplyAgentSimulateResponse {
  supplier_id: number;
  warehouse_id: number;
  organization_id: number;
  status: string;
  before: SupplyAgentOrderSnapshot;
  after: SupplyAgentOrderSnapshot;
  changes: SupplyAgentLineChange[];
  simulation_summary?: SupplyAgentSimulationSummary;
  applied_rules: Record<string, unknown>[];
  lines_detail: Record<string, unknown>[];
  hard_constraints_applied?: Record<string, unknown>[];
  optimization_summary?: Record<string, unknown>;
  reduction_summary?: Record<string, unknown>;
  family_quantity_summary?: SupplyAgentFamilyGlobalSummary;
  family_global_summaries?: SupplyAgentFamilyGlobalSummary[];
  commitment_summary?: SupplyAgentCommitmentSummary | null;
  policy_notes?: string[];
}

export interface SupplyAgentApplyResponse extends SupplyAgentSimulateResponse {
  applied: boolean;
  apply_target?: 'pending_order' | 'cantidad_usuario';
  dry_run?: boolean;
  reason?: string;
  pending_order_id?: number | null;
  pending_order_created?: boolean;
  adjustment_log_id?: number | null;
  updated_items?: Array<{
    product_id: number;
    sku: string;
    pending_order_item_id: number;
    previous_quantity: number | null;
    new_quantity: number;
    suggested_quantity: number;
  }>;
  removed_items?: Array<{ product_id: number }>;
  updated_lines?: Array<{
    product_id: number;
    sku: string;
    suggestion_id: number;
    previous_user_quantity: number | null;
    new_user_quantity: number;
  }>;
  skipped_lines?: Array<Record<string, unknown>>;
}

export interface SupplyAgentSummaryResponse {
  supplier_id: number;
  warehouse_id: number;
  organization_id: number;
  total_items: number;
  total_lines: number;
  total_amount_euros: number;
  lines_detail: Record<string, unknown>[];
}

export interface SupplyAgentRulesCatalogTotals {
  active_rules: number;
  suppliers_with_rules: number;
  warehouse_rules: number;
}

export interface SupplyAgentRulesCatalogSupplier {
  supplier_id: number;
  supplier_name: string;
  active_count: number;
  rules: Record<string, unknown>[];
}

export interface SupplyAgentRulesCatalog {
  warehouse_id: number;
  totals: SupplyAgentRulesCatalogTotals;
  warehouse_rules: Record<string, unknown>[];
  suppliers: SupplyAgentRulesCatalogSupplier[];
}

export const supplyAgentService = {
  async getSummary(supplierId: number, warehouseId: number): Promise<SupplyAgentSummaryResponse> {
    const params = new URLSearchParams({
      supplier_id: String(supplierId),
      warehouse_id: String(warehouseId),
    });
    return fetchApi<SupplyAgentSummaryResponse>(`${BASE}/summary/?${params.toString()}`);
  },

  async simulate(payload: {
    supplier_id: number;
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    use_persisted_rules?: boolean;
    apply_line_box_multiples?: boolean;
    active_rule_ids?: number[] | null;
    locale?: string;
    completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
    reduction_strategy?: 'slow_movers' | 'proportional' | null;
  }): Promise<SupplyAgentSimulateResponse> {
    return fetchApi<SupplyAgentSimulateResponse>(`${BASE}/simulate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async apply(payload: {
    supplier_id: number;
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    use_persisted_rules?: boolean;
    apply_line_box_multiples?: boolean;
    apply_target?: 'pending_order' | 'cantidad_usuario';
    dry_run?: boolean;
    locale?: string;
    completion_strategy?: 'coverage_layers' | 'top_sales' | 'proportional' | 'high_margin' | null;
  }): Promise<SupplyAgentApplyResponse> {
    return fetchApi<SupplyAgentApplyResponse>(`${BASE}/apply/`, {
      method: 'POST',
      body: {
        ...payload,
        apply_target: payload.apply_target ?? 'pending_order',
      },
    });
  },

  async listRules(
    supplierId: number | null | undefined,
    warehouseId: number,
    options?: { includeInactive?: boolean },
  ): Promise<{ rules: Record<string, unknown>[]; count: number }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    if (options?.includeInactive) {
      params.set('include_inactive', 'true');
    }
    return fetchApi<{ rules: Record<string, unknown>[]; count: number }>(`${BASE}/rules/?${params.toString()}`);
  },

  async rulesCatalog(
    warehouseId: number,
    options?: { includeInactive?: boolean },
  ): Promise<SupplyAgentRulesCatalog> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (options?.includeInactive) {
      params.set('include_inactive', 'true');
    }
    return fetchApi<SupplyAgentRulesCatalog>(`${BASE}/rules/catalog/?${params.toString()}`);
  },

  async deleteRule(
    ruleId: number,
    supplierId: number | null | undefined,
    warehouseId: number,
    options?: { hard?: boolean },
  ): Promise<{ deleted: boolean; purged?: boolean; rules: Record<string, unknown>[]; count: number }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    if (options?.hard) {
      params.set('hard', 'true');
    }
    return fetchApi<{ deleted: boolean; purged?: boolean; rules: Record<string, unknown>[]; count: number }>(
      `${BASE}/rules/${ruleId}/?${params.toString()}`,
      { method: 'DELETE' },
    );
  },

  async purgeInactiveRules(
    supplierId: number,
    warehouseId: number,
  ): Promise<{ purged: number; rules: Record<string, unknown>[]; count: number }> {
    return fetchApi<{ purged: number; rules: Record<string, unknown>[]; count: number }>(
      `${BASE}/rules/purge-inactive/`,
      {
        method: 'POST',
        body: { supplier_id: supplierId, warehouse_id: warehouseId },
      },
    );
  },

  async familyOverview(
    warehouseId: number,
    ephemeralRules?: Record<string, unknown>[],
  ): Promise<SupplyAgentFamilyOverviewResponse> {
    if (ephemeralRules?.length) {
      return fetchApi<SupplyAgentFamilyOverviewResponse>(`${BASE}/family-overview/`, {
        method: 'POST',
        body: { warehouse_id: warehouseId, ephemeral_rules: ephemeralRules },
      });
    }
    const params = new URLSearchParams({ warehouse_id: String(warehouseId) });
    return fetchApi<SupplyAgentFamilyOverviewResponse>(
      `${BASE}/family-overview/?${params.toString()}`,
    );
  },

  async warehouseSimulate(payload: {
    warehouse_id: number;
    ephemeral_rules?: Record<string, unknown>[];
    active_rule_ids?: number[];
  }): Promise<SupplyAgentWarehouseSimulateResponse> {
    return fetchApi<SupplyAgentWarehouseSimulateResponse>(`${BASE}/warehouse-simulate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async patchRule(
    ruleId: number,
    supplierId: number | null | undefined,
    warehouseId: number,
    payload: { is_active: boolean },
  ): Promise<{
    updated: boolean;
    rule: Record<string, unknown>;
    rules: Record<string, unknown>[];
    count: number;
  }> {
    const params = new URLSearchParams({
      warehouse_id: String(warehouseId),
    });
    if (supplierId != null) {
      params.set('supplier_id', String(supplierId));
    }
    return fetchApi(`${BASE}/rules/${ruleId}/?${params.toString()}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async interpret(payload: {
    message: string;
    supplier_id?: number;
    warehouse_id?: number;
    run_simulation?: boolean;
    persist_if_requested?: boolean;
    use_llm?: boolean;
    locale?: string;
  }): Promise<Record<string, unknown>> {
    return fetchApi<Record<string, unknown>>(`${BASE}/interpret/`, {
      method: 'POST',
      body: payload,
    });
  },

  async chat(payload: {
    message: string;
    supplier_id?: number;
    warehouse_id?: number;
    run_simulation?: boolean;
    persist_if_requested?: boolean;
    use_llm?: boolean;
    confirm_save?: boolean;
    pending_rules?: Record<string, unknown>[];
    locale?: string;
    conversation_history?: Array<{ role: string; content: string }>;
    signal?: AbortSignal;
  }): Promise<{ reply: string; interpretation: Record<string, unknown> }> {
    const { signal, ...body } = payload;
    return fetchApi<{ reply: string; interpretation: Record<string, unknown> }>(`${BASE}/chat/`, {
      method: 'POST',
      body,
      signal,
    });
  },

  async logisticsMasterStatus(params?: {
    supplier_id?: number | null;
  }): Promise<SupplyAgentLogisticsMasterStatus> {
    const qs =
      params?.supplier_id != null ? `?supplier_id=${encodeURIComponent(params.supplier_id)}` : '';
    return fetchApi<SupplyAgentLogisticsMasterStatus>(`${BASE}/logistics/master-status/${qs}`);
  },

  async uploadLogisticsExcel(
    file: File,
    options?: { replace_existing?: boolean },
  ): Promise<SupplyAgentLogisticsUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.replace_existing) {
      formData.append('replace_existing', 'true');
    }
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/${BASE}/logistics/upload/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(err.detail || res.statusText || 'Error al subir el Excel logístico');
    }
    return res.json();
  },

  async logisticsCalculate(payload: {
    mode?: 'VALIDATE' | 'SIMULATE' | 'APPLY';
    supplier_id?: number | null;
    warehouse_id?: number | null;
    load_suggested_order?: boolean;
    dry_run?: boolean;
    rule_id?: number;
    external_rule_id?: string;
    lines?: Record<string, unknown>[];
    constraint?: Record<string, unknown>;
    strategy?: Record<string, unknown>;
  }): Promise<SupplyAgentLogisticsCalculateResponse> {
    return fetchApi<SupplyAgentLogisticsCalculateResponse>(`${BASE}/logistics/calculate/`, {
      method: 'POST',
      body: payload,
    });
  },

  async logisticsListProducts(supplierId: number): Promise<{
    status: string;
    products: SupplyAgentLogisticsProduct[];
    count: number;
  }> {
    return fetchApi(`${BASE}/logistics/products/?supplier_id=${encodeURIComponent(supplierId)}`);
  },

  async logisticsPatchProduct(
    productEqId: number,
    payload: {
      max_extra_pct?: number | null;
      max_extra_coverage_days?: number | null;
      pallet_family?: string;
    },
  ): Promise<{ status: string; product: SupplyAgentLogisticsProduct }> {
    return fetchApi(`${BASE}/logistics/products/${productEqId}/`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async logisticsListRules(
    supplierId: number,
    options?: { include_inactive?: boolean },
  ): Promise<{ status: string; rules: SupplyAgentLogisticsRule[]; count: number }> {
    const qs = new URLSearchParams({ supplier_id: String(supplierId) });
    if (options?.include_inactive) qs.set('include_inactive', 'true');
    return fetchApi(`${BASE}/logistics/rules/?${qs.toString()}`);
  },

  async logisticsCreateRule(
    payload: SupplyAgentLogisticsRuleWritePayload & { supplier_id: number },
  ): Promise<{ status: string; rule: SupplyAgentLogisticsRule }> {
    return fetchApi(`${BASE}/logistics/rules/`, {
      method: 'POST',
      body: payload,
    });
  },

  async logisticsPatchRule(
    ruleId: number,
    payload: SupplyAgentLogisticsRuleWritePayload,
  ): Promise<{ status: string; rule: SupplyAgentLogisticsRule }> {
    return fetchApi(`${BASE}/logistics/rules/${ruleId}/`, {
      method: 'PATCH',
      body: payload,
    });
  },

  async logisticsListContainers(supplierId: number): Promise<{
    status: string;
    containers: SupplyAgentLogisticsContainer[];
    count: number;
  }> {
    return fetchApi(`${BASE}/logistics/containers/?supplier_id=${encodeURIComponent(supplierId)}`);
  },
};

export interface SupplyAgentLogisticsMasterStatus {
  status: string;
  loaded: boolean;
  supplier_id?: number | null;
  products_count: number;
  rules_count: number;
  containers_count: number;
  rule_types?: string[];
  last_updated?: string | null;
}

export interface SupplyAgentLogisticsProduct {
  id: number;
  supplier_id: number;
  product_id: number;
  product_code: string;
  product_name: string;
  active: boolean;
  pallet_family: string;
  max_extra_pct: number | null;
  max_extra_coverage_days: number | null;
  units_per_box?: number | null;
  boxes_per_base?: number | null;
  bases_per_pallet?: number | null;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsRule {
  id: number;
  supplier_id: number | null;
  external_rule_id: string;
  name: string;
  active: boolean;
  constraint_type: string;
  container_code: string;
  container_template_id?: number | null;
  target_value: number | null;
  minimum_value: number | null;
  maximum_value: number | null;
  constraint_unit: string;
  adjustment_direction: string;
  optimization_strategy: string;
  max_extra_pct_default: number | null;
  max_extra_coverage_days_default: number | null;
  incompatible_product_policy: string;
  allow_mixed_products: boolean;
  priority: number;
  notes: string;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsRuleWritePayload {
  name?: string;
  external_rule_id?: string;
  active?: boolean;
  constraint_type?: string;
  container_code?: string;
  container_template_id?: number | null;
  target_value?: number | null;
  minimum_value?: number | null;
  maximum_value?: number | null;
  constraint_unit?: string;
  adjustment_direction?: string;
  optimization_strategy?: string;
  max_extra_pct_default?: number | null;
  max_extra_coverage_days_default?: number | null;
  incompatible_product_policy?: string;
  allow_mixed_products?: boolean;
  priority?: number;
  notes?: string;
}

export interface SupplyAgentLogisticsContainer {
  id: number;
  supplier_id: number | null;
  container_code: string;
  description: string;
  active: boolean;
  usable_volume_m3: number | null;
  usable_payload_kg: number | null;
  minimum_fill_pct: number | null;
  maximum_fill_pct: number | null;
  capacity_binding_rule: string;
  updated_at?: string | null;
}

export interface SupplyAgentLogisticsUploadSection {
  created: number;
  updated: number;
  errors: Array<{ row?: number; error?: string }>;
  warnings: Array<{ row?: number; warning?: string }>;
}

export interface SupplyAgentLogisticsUploadResponse {
  status: string;
  products: SupplyAgentLogisticsUploadSection;
  rules: SupplyAgentLogisticsUploadSection;
  containers: SupplyAgentLogisticsUploadSection;
  sheets?: { products?: number; rules?: number; containers?: number };
  detail?: string;
}

export interface SupplyAgentLogisticsLineAdjustment {
  product_id: number;
  sku?: string;
  product_name?: string;
  pallet_family?: string;
  temperature_zone?: string;
  old_units: number;
  new_units: number;
  delta_units: number;
  reason?: string;
  reason_code?: string;
  explanation?: string;
  explanation_items?: string[];
  boxes_added?: number;
  pallet_eq_delta?: number;
  pallet_eq_before?: number | null;
  pallet_eq_after?: number | null;
  occupancy_pct_before?: number | null;
  occupancy_pct_after?: number | null;
  occupancy_basis?: string;
  rotation_value?: number;
  desired_pallets?: number;
  gap_before?: number;
  group_label?: string;
}

export interface SupplyAgentLogisticsAlternative {
  strategy: string;
  status?: string;
  summary?: string;
  line_adjustments?: SupplyAgentLogisticsLineAdjustment[];
  warnings?: string[];
}

export interface SupplyAgentLogisticsCalculateResponse {
  mode: string;
  status: string;
  detail?: string;
  organization_id?: number;
  supplier_id?: number | null;
  warehouse_id?: number | null;
  before?: {
    units?: number;
    pallet_equivalent?: number;
    volume_m3?: number;
    weight_kg?: number;
    lines_detail?: Record<string, unknown>[];
  };
  after?: {
    units?: number;
    pallet_equivalent?: number;
    volume_m3?: number;
    weight_kg?: number;
    lines_detail?: Record<string, unknown>[];
  };
  lines_detail?: Record<string, unknown>[];
  changes?: Array<{
    product_id: number;
    sku?: string;
    old_quantity: number;
    new_quantity: number;
    old_value_euros?: number;
    new_value_euros?: number;
    reasons?: Array<{ label?: string; step?: string; rule_type?: string }>;
  }>;
  line_adjustments?: SupplyAgentLogisticsLineAdjustment[];
  alternatives?: SupplyAgentLogisticsAlternative[];
  applied_rules?: Record<string, unknown>[];
  utilization?: {
    pallet_equivalent_before?: number;
    pallet_equivalent_after?: number;
    pallet_fill_pct_after?: number;
    volume_m3_before?: number;
    volume_m3_after?: number;
    volume_fill_pct_after?: number;
    weight_kg_before?: number;
    weight_kg_after?: number;
    weight_fill_pct_after?: number;
    container_target_fill_pct?: number;
    capacity_binding?: string;
  };
  container?: Record<string, unknown>;
  warnings?: string[];
  missing_data?: Array<{ product_id: number; fields: string[] }>;
  eligible_lines_count?: number;
  skipped_lines_count?: number;
  rule?: Record<string, unknown>;
  applied?: boolean;
  dry_run?: boolean;
  apply_skipped_reason?: string;
  apply_result?: {
    applied?: boolean;
    pending_order_id?: number;
    pending_order_created?: boolean;
    updated_items?: unknown[];
    removed_items?: unknown[];
    [key: string]: unknown;
  };
  audit?: Record<string, unknown>;
}
